import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchRequisitionsList,
  setFilters,
  FetchRequisitionsParams,
  RequisitionListItem,
  bulkApproveRequisitions,
  bulkRejectRequisitions,
  RequisitionDetail,
  fetchRequisitionDetails
} from './requisitionSlice';
import { AppDispatch, RootState } from '../../app/store';
import RequisitionCard, { sharedRequisitionDetailsCache, loadingStates } from './RequisitionCard';
import { getStatusDisplay } from '../../utils/statusUtils';
import toast from 'react-hot-toast';
import type { AuthUser } from '../auth/authSlice';
import QuickViewModal from './QuickViewModal';
import { formatCurrency } from '../../utils/formatUtils';
import { fetchStoresApi, fetchCategoriesApi, fetchAreaCoachesApi } from '../../services/api';

// Define a type for the view mode
type ViewMode = 'card' | 'table';

// Interface for store dropdown
interface StoreOption {
  id: number;
  name: string;
  storenumber?: string;
}

// Interface for category dropdown
interface CategoryOption {
  id: number;
  name: string;
}

// Interface for area coach dropdown
interface AreaCoachOption {
  id: number;
  name: string;
  user_role_id: number;
}

// Define an extended interface for requisitions that might include details
// This helps in type-checking when accessing properties like assets, repairs, etc.
// interface ExtendedRequisition extends RequisitionListItem {
//   assets?: AssetItem[];
//   repairs?: RepairItem[];
//   equipmentRequests?: EquipmentRequestItem[];
//   internal?: boolean | number;
//   technician?: { id: number; name: string };
//   supplier?: { id: number; name: string };
// }

// Component for table items preview with auto-loading
interface TableItemsPreviewProps {
  requisitionId: number;
}

interface ProcessedItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  details?: string;
  descriptions?: string[];
  bgColor: string;
  borderColor: string;
  badgeColor: string;
}

const TableItemsPreview: React.FC<TableItemsPreviewProps> = ({ requisitionId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [items, setItems] = useState<ProcessedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Ref for intersection observer
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer to detect when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: '50px' // Start loading 50px before the component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [hasLoaded]);

  // Auto-load items when component becomes visible
  useEffect(() => {
    if (isVisible && !hasLoaded && !loading) {
      // Check cache first
      const cachedItems = sharedRequisitionDetailsCache.get(requisitionId);
      if (cachedItems) {
        setItems(cachedItems);
        setExpanded(true); // Auto-expand when loaded
        setHasLoaded(true);
        return;
      }

      // Check if already loading this requisition
      if (loadingStates.has(requisitionId)) {
        return;
      }

      // Load from API
      setLoading(true);
      loadingStates.add(requisitionId);

      dispatch(fetchRequisitionDetails(requisitionId.toString()))
        .unwrap()
        .then((requisition: RequisitionDetail) => {
          const processedItems: ProcessedItem[] = [];

          // Process equipment items (same logic as QuickViewModal)
          if (requisition.equipmentItems && requisition.equipmentItems.length > 0) {
            requisition.equipmentItems.forEach((item, index) => {
              processedItems.push({
                id: `processed-eq-${index}`,
                type: 'Equipment',
                title: item.asset,
                subtitle: `${item.equipment.category} - ${item.equipment.subcategory}`,
                details: `S/N: ${item.equipment.serial_number}`,
                descriptions: item.descriptions,
                bgColor: 'bg-amber-50 dark:bg-amber-900/30',
                borderColor: 'border-amber-200 dark:border-amber-700',
                badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200'
              });
            });
          }

          // Process asset description pairs
          if (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) {
            requisition.assetDescriptionPairs.forEach((pair, index) => {
              processedItems.push({
                id: `processed-asset-${index}`,
                type: 'Asset',
                title: pair.asset,
                descriptions: pair.descriptions,
                bgColor: 'bg-indigo-50 dark:bg-indigo-900/30',
                borderColor: 'border-indigo-200 dark:border-indigo-700',
                badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
              });
            });
          }

          // Fallback to legacy data if no processed data
          if (processedItems.length === 0) {
            // Equipment requests
            if (requisition.equipmentRequests && requisition.equipmentRequests.length > 0) {
              requisition.equipmentRequests.forEach((item, index) => {
                processedItems.push({
                  id: `eq-${item.id || index}`,
                  type: 'Equipment',
                  title: item.equipment?.description || 'Equipment Item',
                  subtitle: item.equipment?.serial_number ? `S/N: ${item.equipment.serial_number}` : '',
                  details: item.notes || '',
                  bgColor: 'bg-amber-50 dark:bg-amber-900/30',
                  borderColor: 'border-amber-200 dark:border-amber-700',
                  badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200'
                });
              });
            }

            // Assets with descriptions
            if (requisition.assets && requisition.assets.length > 0) {
              requisition.assets.forEach((item, index) => {
                const descriptions = item.descriptions?.map(desc => desc.description) || [];
                processedItems.push({
                  id: `asset-${item.id || index}`,
                  type: 'Asset',
                  title: item.asset,
                  descriptions: descriptions,
                  bgColor: 'bg-indigo-50 dark:bg-indigo-900/30',
                  borderColor: 'border-indigo-200 dark:border-indigo-700',
                  badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
                });
              });
            }

            // Legacy repairs
            if (requisition.repairs && requisition.repairs.length > 0 && (!requisition.assets || requisition.assets.length === 0)) {
              requisition.repairs.forEach((repair, index) => {
                processedItems.push({
                  id: `legacy-repair-${repair.id || index}`,
                  type: 'Legacy',
                  title: `Item ${index + 1}`,
                  descriptions: [repair.description],
                  bgColor: 'bg-purple-50 dark:bg-purple-900/30',
                  borderColor: 'border-purple-200 dark:border-purple-700',
                  badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                });
              });
            }
          }

          // Cache the results
          sharedRequisitionDetailsCache.set(requisitionId, processedItems);

          setItems(processedItems);
          setExpanded(true); // Auto-expand when loaded
          setHasLoaded(true);
          setLoading(false);
          loadingStates.delete(requisitionId);
        })
        .catch(() => {
          setLoading(false);
          setHasLoaded(true); // Mark as loaded even on error to prevent retry loops
          loadingStates.delete(requisitionId);
        });
    }
  }, [isVisible, hasLoaded, loading, requisitionId, dispatch]);

  return (
    <div ref={containerRef}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
      >
        {expanded ? 'Hide' : 'Show'} Items
        {loading && <span className="text-gray-500 ml-1">(Loading...)</span>}
        {items.length > 0 && !loading && <span className="ml-1">({items.length})</span>}
      </button>

      {loading && (
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          <svg className="animate-spin inline -ml-1 mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading items...
        </div>
      )}

      {expanded && hasLoaded && !loading && (
        <div className="mt-1 space-y-1 max-w-xs">
          {items.length > 0 ? (
            items.map((item) => ( // Show all items in table
              <div
                key={item.id}
                className={`p-1.5 border rounded text-xs ${item.bgColor} ${item.borderColor}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-gray-200 truncate">
                      {item.title}
                    </p>
                    {item.subtitle && (
                      <p className="text-gray-600 dark:text-gray-400 truncate">
                        {item.subtitle}
                      </p>
                    )}
                    {item.descriptions && item.descriptions.length > 0 && (
                      <p className="text-gray-600 dark:text-gray-400 truncate">
                        {item.descriptions[0]}
                      </p>
                    )}
                  </div>
                  <span className={`px-1 py-0.5 text-xs rounded ${item.badgeColor} ml-1 flex-shrink-0`}>
                    {item.type.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No items listed.</p>
          )}
        </div>
      )}
    </div>
  );
};

const RequisitionListPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  
  // Ref to track if initial load has been done
  const hasLoadedInitial = useRef(false);

  const {
    pagination,
    isLoadingList,
    error,
    filters
  } = useSelector((state: RootState) => state.requisitions);
  const currentUser = useSelector((state: RootState) => state.auth.user as AuthUser | null);

  // Define localStorage key for filters
  const FILTERS_STORAGE_KEY = 'requisition_filters';
  const VIEW_MODE_STORAGE_KEY = 'requisition_view_mode';

  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  // Add state for job card required filter
  const [showJobCardRequiredOnly, setShowJobCardRequiredOnly] = useState<boolean>(false);

  // API data for dropdowns - Initialize with empty arrays to prevent undefined errors
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [areaCoaches, setAreaCoaches] = useState<AreaCoachOption[]>([]);
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<number | null>(null);

  // Loading states for dropdowns
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingAreaCoaches, setIsLoadingAreaCoaches] = useState(false);

  // State for store search in dropdown
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);

  // Ref to track the dropdown container
  const storeDropdownRef = useRef<HTMLDivElement>(null);
  const mobileStoreDropdownRef = useRef<HTMLDivElement>(null);

  // Local state for controlled form inputs, initialized from Redux state
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const [localStatus, setLocalStatus] = useState(filters.status || 'pending_my_approval');
  const [localDateFrom, setLocalDateFrom] = useState(filters.date_from || '');
  const [localDateTo, setLocalDateTo] = useState(filters.date_to || '');

  // Add states for selection and modals
  const [selectedRequisitions, setSelectedRequisitions] = useState<number[]>([]);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  // Add state for quick view modal
  const [quickViewRequisitionId, setQuickViewRequisitionId] = useState<number | null>(null);

  // Add mobile filter drawer state
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  // Add desktop filter collapsible state (closed by default)
  const [desktopFiltersVisible, setDesktopFiltersVisible] = useState(false);

  // Ref for the master checkbox in the table header
  const masterCheckboxRef = useRef<HTMLInputElement>(null);

  // Debounce timer ref for search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // State to track if stores selection has changed while dropdown was open
  const [storesChangedWhileOpen, setStoresChangedWhileOpen] = useState(false);

  // Bulk action loading & per-ID processing state
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);

  // Add optimistic UI updates for better perceived performance
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // API loading functions
  const loadStores = useCallback(async () => {
    setIsLoadingStores(true);
    try {
      const storesData = await fetchStoresApi();
      setStores(Array.isArray(storesData) ? storesData : []);
    } catch (error) {
      console.error('Failed to load stores:', error);
      toast.error('Failed to load stores');
      setStores([]);
    } finally {
      setIsLoadingStores(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const categoriesData = await fetchCategoriesApi();
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  const loadAreaCoaches = useCallback(async () => {
    setIsLoadingAreaCoaches(true);
    try {
      const areaCoachesData = await fetchAreaCoachesApi();
      // Map the API response to the expected format
      const mappedCoaches = Array.isArray(areaCoachesData)
        ? areaCoachesData.map(coach => ({
            id: coach.id,
            name: coach.name,
            user_role_id: coach.user_role_id || 0
          }))
        : [];
      setAreaCoaches(mappedCoaches);
    } catch (error) {
      console.error('Failed to load area coaches:', error);
      toast.error('Failed to load area coaches');
      setAreaCoaches([]);
    } finally {
      setIsLoadingAreaCoaches(false);
    }
  }, []);

  const loadRequisitions = useCallback((page = 1, searchTerm?: string) => {
    const currentParams: FetchRequisitionsParams = {
      ...filters,
      page,
      search: searchTerm !== undefined ? searchTerm : filters.search,
      status: localStatus,
      date_from: localDateFrom,
      date_to: localDateTo,
      store_ids: selectedStores,
      category_id: selectedCategory,
      area_coach_id: selectedAreaCoach,
      showJobCardRequiredOnly: showJobCardRequiredOnly,
    };
    dispatch(fetchRequisitionsList(currentParams));
  }, [dispatch, filters, localStatus, localDateFrom, localDateTo, selectedStores, selectedCategory, selectedAreaCoach, showJobCardRequiredOnly]);

  // Load dropdown data and initial requisitions
  useEffect(() => {
    // Prevent multiple initial loads
    if (hasLoadedInitial.current) return;
    
    const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        // Only dispatch if we have valid filters and they're different from current
        if (parsedFilters && Object.keys(parsedFilters).length > 0) {
          dispatch(setFilters({ ...parsedFilters }));
        }
        // Update local state
        setLocalSearch(parsedFilters.search || '');
        setLocalStatus(parsedFilters.status || 'pending_my_approval');
        setLocalDateFrom(parsedFilters.date_from || '');
        setLocalDateTo(parsedFilters.date_to || '');
        setSelectedStores(Array.isArray(parsedFilters.store_ids) ? parsedFilters.store_ids : []);
        setSelectedCategory(parsedFilters.category_id || null);
        setSelectedAreaCoach(parsedFilters.area_coach_id || null);
        setShowJobCardRequiredOnly(parsedFilters.showJobCardRequiredOnly || false);
        // Also set the current page if it was saved
        if (parsedFilters.page) {
          setCurrentPage(parsedFilters.page);
        }
      } catch (error) {
        console.error('Failed to parse saved filters:', error);
        // Clear corrupted localStorage data
        localStorage.removeItem(FILTERS_STORAGE_KEY);
      }
    }

    const savedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode;
    if (savedViewMode && (savedViewMode === 'card' || savedViewMode === 'table')) {
      setViewMode(savedViewMode);
    }

    // Load dropdown data in parallel and then load requisitions
    Promise.all([
      loadStores(),
      loadCategories(), 
      loadAreaCoaches()
    ]).then(() => {
      // Load initial requisitions after dropdown data is ready
      loadRequisitions(1); // Start with page 1
      setIsInitialLoad(false);
      hasLoadedInitial.current = true; // Mark initial load as done
    }).catch(error => {
      console.error('Failed to load initial data:', error);
      // Still try to load requisitions even if dropdown data fails
      loadRequisitions(1); // Start with page 1
      setIsInitialLoad(false);
      hasLoadedInitial.current = true; // Mark initial load as done
    });
  }, []); // Remove all dependencies to prevent infinite loop

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      const filtersToSave = {
        search: filters.search,
        status: filters.status,
        date_from: filters.date_from,
        date_to: filters.date_to,
        store_ids: Array.isArray(selectedStores) ? selectedStores : [],
        category_id: selectedCategory,
        area_coach_id: selectedAreaCoach,
        showJobCardRequiredOnly,
        page: currentPage, // Save the current page
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  }, [filters, selectedStores, selectedCategory, selectedAreaCoach, currentPage, showJobCardRequiredOnly]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch (error) {
      console.error('Failed to save view mode to localStorage:', error);
    }
  }, [viewMode]);

  // Sync local state with Redux filters
  useEffect(() => {
    setLocalSearch(filters.search || '');
    setLocalStatus(filters.status || 'pending_my_approval');
    setLocalDateFrom(filters.date_from || '');
    setLocalDateTo(filters.date_to || '');
  }, [filters]);

  // Reset job card filter when status changes to non-approved
  useEffect(() => {
    if (localStatus !== '2' && showJobCardRequiredOnly) {
      setShowJobCardRequiredOnly(false);
    }
  }, [localStatus, showJobCardRequiredOnly]);

  // Update the indeterminate state of the master checkbox
  useEffect(() => {
    if (masterCheckboxRef.current) {
      const currentTableRequisitions = pagination?.data || [];
      // Filter to only selectable requisitions (same logic as in table render)
      const selectableRequisitions = currentTableRequisitions.filter(req => {
        const nextPendingApprover = req.approvers?.find(app => app.status_id === 1);
        return currentUser &&
               req.submitted &&
               req.status_id === 1 &&
               nextPendingApprover &&
               nextPendingApprover.user_id === currentUser.id;
      });

      const numSelectedOnPage = selectableRequisitions.filter(req => selectedRequisitions.includes(req.id)).length;
      const allSelectedOnPage = numSelectedOnPage === selectableRequisitions.length && selectableRequisitions.length > 0;
      masterCheckboxRef.current.indeterminate = numSelectedOnPage > 0 && !allSelectedOnPage;
    }
  }, [selectedRequisitions, pagination?.data, currentUser]);

  // Debounced search handler
  const handleSearchChange = useCallback((searchValue: string) => {
    setLocalSearch(searchValue);

    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout for debounced search
    searchDebounceRef.current = setTimeout(() => {
      dispatch(setFilters({ ...filters, search: searchValue, page: 1 }));
    }, 500); // 500ms delay
  }, [dispatch, filters]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Load requisitions when filters change (but not on initial mount)
  useEffect(() => {
    if (!isInitialLoad) {
      loadRequisitions(filters.page);
    }
  }, [filters, loadRequisitions, isInitialLoad]);

  const handleFilterApply = useCallback(() => {
    // Clear any pending search debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const newFilters: Partial<FetchRequisitionsParams> = {
      search: localSearch,
      status: localStatus,
      date_from: localDateFrom,
      date_to: localDateTo,
      store_ids: selectedStores,
      category_id: selectedCategory,
      area_coach_id: selectedAreaCoach,
      showJobCardRequiredOnly: showJobCardRequiredOnly,
      page: 1,
    };
    dispatch(setFilters(newFilters));
    
    // Close mobile filters drawer
    if (mobileFiltersVisible) {
      setMobileFiltersVisible(false);
    }
    
    // Close desktop filters section
    if (desktopFiltersVisible) {
      setDesktopFiltersVisible(false);
    }
  }, [dispatch, localSearch, localStatus, localDateFrom, localDateTo, selectedStores, selectedCategory, selectedAreaCoach, showJobCardRequiredOnly, mobileFiltersVisible, desktopFiltersVisible]);

  const handleResetFilters = () => {
    setLocalSearch('');
    setLocalStatus('pending_my_approval');
    setLocalDateFrom('');
    setLocalDateTo('');
    setSelectedStores([]);
    setSelectedCategory(null);
    setSelectedAreaCoach(null);
    setShowJobCardRequiredOnly(false);
    dispatch(setFilters({
        search: '',
        status: 'pending_my_approval',
        date_from: '',
        date_to: '',
        page: 1,
        per_page: filters.per_page,
        showJobCardRequiredOnly: false
    }));
    if (mobileFiltersVisible) {
      setMobileFiltersVisible(false); // Close mobile filter drawer after resetting
    }
  };

  const handleStoreToggle = (storeId: number) => {
    setSelectedStores(prev =>
      prev.includes(storeId)
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
    // Clear the search term after selecting/deselecting a store
    setStoreSearchTerm('');
    // Mark that stores selection has changed while dropdown is open
    setStoresChangedWhileOpen(true);
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage > 0 && newPage <= pagination.last_page) {
      setCurrentPage(newPage);
      loadRequisitions(newPage);
    }
  };

  const getDisplayError = (err: unknown): string | null => {
    if (!err) return null;
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null && 'message' in err && typeof (err as any).message === 'string') {
      return (err as any).message;
    }
    return 'An unknown error occurred while fetching requisitions.';
  };

  const displayError = getDisplayError(error);

  const statusOptions = [
    { value: 'pending_my_approval', label: 'Pending My Approval' },
    { value: '1', label: 'Pending (Submitted)' },
    { value: '2', label: 'Approved' },
    { value: '3', label: 'Rejected' },
    { value: '4', label: 'Cancelled' },
    { value: 'all_from_my_stores', label: 'All From My Stores' },
  ];

  // Filter stores based on search term (search both name and store number)
  const filteredStores = (Array.isArray(stores) ? stores : []).filter(store =>
    store.name.toLowerCase().includes(storeSearchTerm.toLowerCase()) ||
    (store.storenumber && store.storenumber.toLowerCase().includes(storeSearchTerm.toLowerCase()))
  );

  // Add selection handling
  const handleSelectRequisition = (requisitionId: number) => {
    setSelectedRequisitions(prev =>
      prev.includes(requisitionId)
        ? prev.filter(id => id !== requisitionId)
        : [...prev, requisitionId]
    );
  };

  const handleSelectAll = (currentRequisitions: RequisitionListItem[]) => {
    const currentRequisitionIds = currentRequisitions.map(req => req.id);
    const allSelectedOnPage = currentRequisitionIds.every(id => selectedRequisitions.includes(id)) && currentRequisitionIds.length > 0;

    if (allSelectedOnPage) {
      // If all on current page are selected, deselect them
      setSelectedRequisitions(prev => prev.filter(id => !currentRequisitionIds.includes(id)));
    } else {
      // Otherwise, select all on current page that are not already selected
      setSelectedRequisitions(prev => Array.from(new Set([...prev, ...currentRequisitionIds])));
    }
  };

  // Bulk actions handlers
  const handleBulkApprove = async () => {
    if (selectedRequisitions.length === 0 || isBulkApproving || isBulkRejecting) return;

    setIsBulkApproving(true);

    try {
      setApproveModalOpen(false);
      await dispatch(bulkApproveRequisitions(selectedRequisitions)).unwrap();
      toast.success(`Successfully approved ${selectedRequisitions.length} requisitions`);
      setSelectedRequisitions([]);
      // Reload the list to get fresh data
      loadRequisitions(currentPage);
    } catch (err) {
      const message = getDisplayError(err) || 'Failed to approve requisitions';
      toast.error(message);
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedRequisitions.length === 0 || !rejectionReason.trim() || isBulkApproving || isBulkRejecting) return;

    setIsBulkRejecting(true);

    try {
      setRejectModalOpen(false);
      await dispatch(bulkRejectRequisitions({
        ids: selectedRequisitions,
        comment: rejectionReason
      })).unwrap();
      toast.success(`Successfully rejected ${selectedRequisitions.length} requisitions`);
      setSelectedRequisitions([]);
      setRejectionReason('');
      // Reload the list to get fresh data
      loadRequisitions(currentPage);
    } catch (err) {
      const message = getDisplayError(err) || 'Failed to reject requisitions';
      toast.error(message);
    } finally {
      setIsBulkRejecting(false);
    }
  };

  // Helper function to check if a requisition requires a job card
  const isJobCardRequired = useCallback((req: RequisitionListItem) => {
    const anyReq: any = req;
    let count = 0;

    // Check all possible job card locations
    if (Array.isArray(anyReq.jobCards)) count = anyReq.jobCards.length;
    else if (Array.isArray(anyReq.jobcards)) count = anyReq.jobcards.length;
    else if (Array.isArray(anyReq.job_cards)) count = anyReq.job_cards.length;
    else if (anyReq.order) {
      if (Array.isArray(anyReq.order.jobCards)) count = anyReq.order.jobCards.length;
      else if (Array.isArray(anyReq.order.jobcards)) count = anyReq.order.jobcards.length;
      else if (Array.isArray(anyReq.order.job_cards)) count = anyReq.order.job_cards.length;
    }

    // Check for single job card object
    if (count === 0 && (anyReq.jobCard || anyReq.job_card)) count = 1;

    // A job card is required if there's an order number but no job cards
    return count === 0 && req.order?.order_number;
  }, []);

  // Helper function to check if a requisition has the selected area coach in its approvers chain
  const hasAreaCoachInApprovers = useCallback((req: RequisitionListItem) => {
    // If no area coach is selected or requisition has no approvers, return true (no filtering)
    if (!selectedAreaCoach || !req.approvers || !Array.isArray(req.approvers)) return true;

    // Check if any approver's user_id matches the selected area coach ID
    return req.approvers.some(approver => approver.user_id === selectedAreaCoach);
  }, [selectedAreaCoach]);

  // Filter requisitions based on job card requirement and area coach
  const filterRequisitions = useCallback((requisitions: RequisitionListItem[]) => {
    return requisitions.filter(req => {
      // Apply job card filter if enabled
      const passesJobCardFilter = !showJobCardRequiredOnly || isJobCardRequired(req);

      // Apply area coach filter if an area coach is selected
      const passesAreaCoachFilter = hasAreaCoachInApprovers(req);

      // Return true only if both filters pass
      return passesJobCardFilter && passesAreaCoachFilter;
    });
  }, [showJobCardRequiredOnly, isJobCardRequired, hasAreaCoachInApprovers]);

  const renderRequisitionTable = (currentRequisitions: RequisitionListItem[]) => {
    // Filter requisitions by job card required if the filter is active
    const filteredRequisitions = filterRequisitions(currentRequisitions);

    // Check if any requisitions can be selected (same logic as individual checkboxes)
    const selectableRequisitions = filteredRequisitions.filter(req => {
      const nextPendingApprover = req.approvers?.find(app => app.status_id === 1);
      return currentUser &&
             req.submitted &&
             req.status_id === 1 &&
             nextPendingApprover &&
             nextPendingApprover.user_id === currentUser.id;
    });

    const hasSelectableRequisitions = selectableRequisitions.length > 0;

    return (
      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="min-w-full bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                {hasSelectableRequisitions ? (
                  <input
                    type="checkbox"
                    ref={masterCheckboxRef}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={selectableRequisitions.length > 0 && selectableRequisitions.every(req => selectedRequisitions.includes(req.id))}
                    onChange={() => handleSelectAll(selectableRequisitions)}
                  />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Store</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Items</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quotation</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Job Cards</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Supplier/Technician</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order #</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Updated</th>
              <th className="px-4 py-3 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredRequisitions.map(req => {
              const { statusText, bgColor, textColor } = getStatusDisplay(req);

              // Updated canSelect logic for table row
              const nextPendingApprover = req.approvers?.find(app => app.status_id === 1);
              const canSelect = currentUser &&
                                req.submitted &&
                                req.status_id === 1 &&
                                nextPendingApprover &&
                                nextPendingApprover.user_id === currentUser.id;

              return (
                <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ease-in-out">
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {canSelect ? (
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={selectedRequisitions.includes(req.id)}
                        onChange={() => handleSelectRequisition(req.id)}
                      />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {new Date(req.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                      {statusText}
                    </span>
                    {req.submitted && req.status_id === 1 && req.approvers && req.approvers.length > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Next: {req.approvers.find(appr => appr.status_id === 1)?.user.name || 'N/A'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b text-sm text-gray-500 dark:text-gray-300">
                    {req.stores.map(s => `${s.name}${s.storenumber ? ` (${s.storenumber})` : ''}`).join(', ')}
                  </td>
                  <td className="px-4 py-3 border-b text-sm text-gray-500 dark:text-gray-300">
                    {req.category?.name || ((req as any).requisition_category_id === 1 ? 'Pots/Fryers/Toasters' : 'N/A')}
                    {req.subcategory?.name ? ` - ${req.subcategory.name}` : ((req as any).requisition_sub_category_id === 19 ? ' - Toaster' : '')}
                    <div className="mt-1">
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">
                        {(req as any).is_repair === 1 ? 'Repair' : 'New Purchase'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 border-b text-sm">
                    <TableItemsPreview requisitionId={req.id} />
                  </td>
                  <td className="px-4 py-3 border-b text-sm text-gray-500 dark:text-gray-300">
                    {req.quotation ? (
                      <span className="text-green-600 dark:text-green-400 font-semibold">
                        {formatCurrency(req.quotation.amount)}
                      </span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  {/* Job Cards Count Cell */}
                  <td className="px-4 py-3 border-b text-sm text-gray-500 dark:text-gray-300">
                    {(() => {
                      const anyReq: any = req;
                      let count = 0;
                      if (Array.isArray(anyReq.jobCards)) count = anyReq.jobCards.length;
                      else if (Array.isArray(anyReq.jobcards)) count = anyReq.jobcards.length;
                      else if (Array.isArray(anyReq.job_cards)) count = anyReq.job_cards.length;
                      else if (anyReq.order) {
                        if (Array.isArray(anyReq.order.jobCards)) count = anyReq.order.jobCards.length;
                        else if (Array.isArray(anyReq.order.jobcards)) count = anyReq.order.jobcards.length;
                        else if (Array.isArray(anyReq.order.job_cards)) count = anyReq.order.job_cards.length;
                      }
                      if (count === 0 && (anyReq.jobCard || anyReq.job_card)) count = 1;

                      // Check if job card is required
                      const isJobCardRequired = count === 0 && req.order?.order_number;

                      return count > 0 || isJobCardRequired ? (
                        <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100">
                          <span className="font-medium mr-1">Job Cards:</span>
                          {count > 0 ? count : <span className="text-red-600 dark:text-red-300 font-semibold">Required</span>}
                        </div>
                      ) : <span className="text-gray-400">0</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3 border-b text-sm text-gray-500 dark:text-gray-300">
                    {req.internal !== undefined ?
                      (req.internal === 1 ?
                        <span className="text-xs">{req.technician?.name || 'N/A'}</span> :
                        <span className="text-xs">{req.supplier?.name || 'N/A'}</span>
                      ) : 'N/A' /* Fallback if internal is not defined */
                    }
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {(req.order?.order_number || req.order?.reference_number) ? (
                      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                        <span className="font-medium mr-1">{req.order?.order_number ? 'Order #:' : 'Ref #:'}</span>
                        {req.order?.order_number || req.order?.reference_number}
                      </div>
                    ) : <span className="text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {new Date(req.updated_at).toLocaleString()}
                  </td>
                   <td className="px-4 py-3 border-b whitespace-nowrap text-sm font-medium">
                    <Link to={`/requisitions/${req.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">
                      View
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRequisitionCards = (currentRequisitions: RequisitionListItem[]) => (
    <div className="grid grid-cols-1 gap-4">
      {filterRequisitions(currentRequisitions).map((requisition) => (
        <RequisitionCard
          key={requisition.id}
          requisition={requisition}
          currentUser={currentUser}
          isSelected={selectedRequisitions.includes(requisition.id)}
          onSelect={handleSelectRequisition}
          onQuickView={setQuickViewRequisitionId}
        />
      ))}
    </div>
  );

  // Handle mobile filters toggle
  const toggleMobileFilters = () => {
    setMobileFiltersVisible(!mobileFiltersVisible);
  };

  // Handle desktop filters toggle
  const toggleDesktopFilters = () => {
    setDesktopFiltersVisible(!desktopFiltersVisible);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isWithinDesktopDropdown = storeDropdownRef.current && storeDropdownRef.current.contains(target);
      const isWithinMobileDropdown = mobileStoreDropdownRef.current && mobileStoreDropdownRef.current.contains(target);

      if (!isWithinDesktopDropdown && !isWithinMobileDropdown && isStoreDropdownOpen) {
        // Close dropdown
        setIsStoreDropdownOpen(false);
        // If stores selection changed while dropdown was open, apply filters
        if (storesChangedWhileOpen) {
          handleFilterApply();
          setStoresChangedWhileOpen(false);
        }
      }
    };

    if (isStoreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStoreDropdownOpen, storesChangedWhileOpen, handleFilterApply]);

  // Handle Apply Stores Filter button click
  const handleApplyStoresFilter = () => {
    setIsStoreDropdownOpen(false);
    handleFilterApply();
    setStoresChangedWhileOpen(false);
  };

  // Handle Clear Stores Filter button click
  const handleClearStoresFilter = () => {
    setSelectedStores([]);
    setIsStoreDropdownOpen(false);
    setStoresChangedWhileOpen(false);
  };

  // Handle Clear All button click
  const handleClearAllStores = () => {
    setSelectedStores([]);
    setStoresChangedWhileOpen(true);
  };

  // Enhanced full-page loader: Show when actively loading AND there's no data currently shown
  // and no error is being displayed.
  if (isLoadingList && !pagination?.data?.length && !displayError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] p-8">
        <svg className="animate-spin -ml-1 mr-3 h-10 w-10 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">
          {isInitialLoad ? 'Loading requisitions...' : 'Loading requisitions...'}
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {isInitialLoad ? 'Preparing your dashboard...' : 'Applying filters...'}
        </p>
      </div>
    );
  }
  // Informative message when loading but some data (possibly stale) is already present
  // This is shown above the filter sections if applicable.
  const ListLoadingIndicator = () => {
    if (isLoadingList && pagination && pagination.data.length > 0 && !displayError) {
      return (
        <div className="my-4 p-3 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded-md shadow text-center flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">
            {isInitialLoad ? 'Loading...' : 'Updating...'}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container mx-auto px-4 pb-12 pt-2">
      <div className="w-full flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center bg-gradient-to-br from-white/95 via-orange-50/30 to-orange-100/40 dark:bg-gradient-to-br dark:from-midnight-900/80 dark:via-midnight-800/70 dark:to-orange-900/80 border border-slate-200/80 dark:border-white/5 rounded-3xl px-6 py-5 shadow-xl shadow-slate-200/60 dark:shadow-soft text-slate-900 dark:text-white mb-6 transition-colors">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-slate-500 dark:text-slate-400">Workflow</p>
          <h2 className="text-3xl font-heading font-semibold tracking-tight">Requisitions Board</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Add filter toggle button for mobile */}
          <button
            onClick={toggleMobileFilters}
            className="md:hidden px-4 py-2 rounded-full text-sm font-semibold bg-slate-900/10 text-slate-900 hover:bg-slate-900/15 dark:bg-white/15 dark:text-white dark:hover:bg-white/25 backdrop-blur"
          >
            {mobileFiltersVisible ? 'Hide Filters' : 'Show Filters'}
          </button>

          <div className="inline-flex rounded-full bg-slate-200/70 dark:bg-white/10 p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${viewMode === 'card' ? 'bg-gradient-to-r from-brand-400 to-brand-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white'}`}
            >
              Card
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${viewMode === 'table' ? 'bg-gradient-to-r from-brand-400 to-brand-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Display list loading indicator if applicable (when data exists but is refreshing) */}
      <ListLoadingIndicator />

      {/* Main content wrapper with conditional padding */}
      <div className={selectedRequisitions.length > 0 ? 'pb-24 sm:pb-20' : ''}>
        {/* Bulk Action Buttons */}
        {selectedRequisitions.length > 0 && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-4xl">
            <div className="bg-white/95 dark:bg-midnight-900/90 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl px-4 py-3 shadow-2xl text-slate-900 dark:text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-colors">
              <div className="font-semibold tracking-wide text-sm uppercase text-slate-600 dark:text-slate-200">
                {selectedRequisitions.length} {selectedRequisitions.length === 1 ? 'requisition' : 'requisitions'} selected
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  disabled={isBulkApproving || isBulkRejecting}
                  onClick={() => setApproveModalOpen(true)}
                  className={`px-4 py-2 rounded-full font-semibold inline-flex items-center gap-2 transition ${isBulkApproving ? 'bg-accent-600/40 cursor-not-allowed' : 'bg-gradient-to-r from-accent-400 to-accent-500 hover:from-accent-300 hover:to-accent-500 text-midnight-950'}`}
                >
                  {isBulkApproving && (
                    <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-midnight-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {selectedRequisitions.length > 1 ? 'Bulk ' : ''}Approve ({selectedRequisitions.length})
                </button>
                <button
                  disabled={isBulkApproving || isBulkRejecting}
                  onClick={() => setRejectModalOpen(true)}
                  className={`px-4 py-2 rounded-full font-semibold inline-flex items-center gap-2 transition ${isBulkRejecting ? 'bg-brand-600/40 cursor-not-allowed' : 'bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500'}`}
                >
                  {isBulkRejecting && (
                    <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {selectedRequisitions.length > 1 ? 'Bulk ' : ''}Reject ({selectedRequisitions.length})
                </button>
                <button
                  disabled={isBulkApproving || isBulkRejecting}
                  onClick={() => setSelectedRequisitions([])}
                  className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-white/20 dark:text-white dark:hover:bg-white/10 transition"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Filter Drawer - only visible on mobile when toggled */}
        <div className={`md:hidden fixed inset-0 z-40 ${mobileFiltersVisible ? 'block' : 'hidden'}`}>
          {/* Backdrop with click-away to close */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={toggleMobileFilters} // Close when backdrop is clicked
          ></div>

          {/* Drawer - slide from bottom */}
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-lg shadow-lg transform transition-transform p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">Filters</h3>
              <button
                onClick={toggleMobileFilters} // Close button for the drawer
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Filter content - reuse the same filter controls */}
            <div className="space-y-4">
              {/* Search Input */}
              <div>
                <label htmlFor="mobile-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  id="mobile-search"
                  value={localSearch}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by store, category, user..."
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label htmlFor="mobile-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id="mobile-status"
                  value={localStatus}
                  onChange={(e) => setLocalStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label htmlFor="mobile-date-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  id="mobile-date-from"
                  value={localDateFrom}
                  onChange={(e) => setLocalDateFrom(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              {/* Date To */}
              <div>
                <label htmlFor="mobile-date-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  id="mobile-date-to"
                  value={localDateTo}
                  onChange={(e) => setLocalDateTo(e.target.value)}
                  min={localDateFrom} // Ensure Date To is not before Date From
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              {/* Store Filter */}
              <div className="relative" ref={mobileStoreDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Stores {selectedStores.length > 0 && `(${selectedStores.length} selected)`}
                  {isLoadingStores && <span className="text-xs text-gray-500"> (Loading...)</span>}
                </label>
                <input
                  type="text"
                  placeholder="Search stores by name or number..."
                  value={storeSearchTerm}
                  onChange={(e) => setStoreSearchTerm(e.target.value)}
                  onFocus={() => setIsStoreDropdownOpen(true)}
                  disabled={isLoadingStores}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                />
                {isStoreDropdownOpen && !isLoadingStores && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md border border-gray-300 dark:border-gray-600 max-h-48 overflow-auto"
                  >
                    {filteredStores.length > 0 ? (
                      filteredStores.map(store => (
                        <div
                          key={store.id}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center text-gray-900 dark:text-white"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStoreToggle(store.id);
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`mobile-store-${store.id}`}
                            checked={selectedStores.includes(store.id)}
                            readOnly
                            className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label htmlFor={`mobile-store-${store.id}`} className="cursor-pointer text-sm text-gray-900 dark:text-white">
                            {store.name}{store.storenumber && ` (${store.storenumber})`}
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500 dark:text-gray-400 text-sm">No stores found</div>
                    )}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedStores.map(storeId => {
                    const store = (Array.isArray(stores) ? stores : []).find(s => s.id === storeId);
                    return store ? (
                      <span
                        key={storeId}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100"
                      >
                        {store.name}{store.storenumber && ` (${store.storenumber})`}
                        <button
                          type="button"
                          className="ml-1.5 inline-flex text-blue-500 dark:text-blue-200 hover:text-blue-700 dark:hover:text-blue-100"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStoreToggle(storeId);
                          }}
                        >
                          &times;
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
                {isStoreDropdownOpen && (
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={handleApplyStoresFilter}
                      className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      Apply Stores Filter
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllStores}
                      className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div>
                <label htmlFor="mobile-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category {isLoadingCategories && <span className="text-xs text-gray-500">(Loading...)</span>}
                </label>
                <select
                  id="mobile-category"
                  value={selectedCategory || ''}
                  onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                  disabled={isLoadingCategories}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                >
                  <option value="">All Categories</option>
                  {(Array.isArray(categories) ? categories : []).map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Area Coach Filter - Visible for all users */}
              <div>
                <label htmlFor="mobile-area-coach" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Area Coach {isLoadingAreaCoaches && <span className="text-xs text-gray-500">(Loading...)</span>}
                </label>
                <select
                  id="mobile-area-coach"
                  value={selectedAreaCoach || ''}
                  onChange={(e) => setSelectedAreaCoach(e.target.value ? Number(e.target.value) : null)}
                  disabled={isLoadingAreaCoaches}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                >
                  <option value="">All Area Coaches</option>
                  {(Array.isArray(areaCoaches) ? areaCoaches : []).map(coach => (
                    <option key={coach.id} value={coach.id}>
                      {coach.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Job Card Required Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Job Card Filter
                  {localStatus !== '2' && (
                    <span className="text-xs text-gray-500 ml-1">(Only available for Approved status)</span>
                  )}
                </label>
                <div className={`flex items-center p-2 border ${localStatus === '2' ? 'border-gray-300' : 'border-gray-200'} rounded-md shadow-sm ${localStatus === '2' ? 'dark:bg-gray-700' : 'bg-gray-100 dark:bg-gray-800'} dark:border-gray-600`}>
                  <input
                    type="checkbox"
                    id="mobile-job-card-required"
                    checked={showJobCardRequiredOnly}
                    onChange={(e) => localStatus === '2' ? setShowJobCardRequiredOnly(e.target.checked) : null}
                    disabled={localStatus !== '2'}
                    className={`h-4 w-4 ${localStatus === '2' ? 'text-blue-600' : 'text-gray-400'} rounded border-gray-300 focus:ring-blue-500`}
                  />
                  <label htmlFor="mobile-job-card-required" className={`ml-2 text-sm ${localStatus === '2' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    Show only requisitions requiring job cards
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={handleResetFilters} // Reset filters and close drawer
                  disabled={isLoadingList}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center"
                >
                  {isLoadingList && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Reset
                </button>
                <button
                  onClick={handleFilterApply} // Apply filters and close drawer
                  disabled={isLoadingList}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {isLoadingList && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Filter Section - only visible on larger screens */}
        <div className="hidden md:block mb-6 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md bg-white dark:bg-gray-800/50">
          {/* Collapsible Header */}
          <button
            onClick={toggleDesktopFilters}
            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-t-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">Filters</h3>
            <div className="flex items-center space-x-2">
              {/* Show active filters count */}
              {(localSearch || localStatus !== 'pending_my_approval' || localDateFrom || localDateTo || 
                selectedStores.length > 0 || selectedCategory || selectedAreaCoach || showJobCardRequiredOnly) && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-brand-100 text-brand-800 dark:bg-brand-800 dark:text-brand-100">
                  Active
                </span>
              )}
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${desktopFiltersVisible ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Collapsible Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${desktopFiltersVisible ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="p-4 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Search Input */}
                <div>
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    id="search"
                    value={localSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search by store, category, user..."
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    id="status"
                    value={localStatus}
                    onChange={(e) => setLocalStatus(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date From
                  </label>
                  <input
                    type="date"
                    id="date-from"
                    value={localDateFrom}
                    onChange={(e) => setLocalDateFrom(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date To
                  </label>
                  <input
                    type="date"
                    id="date-to"
                    value={localDateTo}
                    onChange={(e) => setLocalDateTo(e.target.value)}
                    min={localDateFrom} // Ensure Date To is not before Date From
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {/* Store Filter (Searchable Dropdown) */}
                <div className="relative" ref={storeDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stores {selectedStores.length > 0 && `(${selectedStores.length} selected)`}
                    {isLoadingStores && <span className="text-xs text-gray-500"> (Loading...)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search stores by name or number..."
                      value={storeSearchTerm}
                      onChange={(e) => setStoreSearchTerm(e.target.value)}
                      onFocus={() => setIsStoreDropdownOpen(true)}
                      disabled={isLoadingStores}
                      className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                    />
                    {isStoreDropdownOpen && !isLoadingStores && (
                      <div
                        className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md border border-gray-300 dark:border-gray-600 max-h-48 overflow-auto"
                      >
                        {filteredStores.length > 0 ? (
                          filteredStores.map(store => (
                            <div
                              key={store.id}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center text-gray-900 dark:text-white"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleStoreToggle(store.id);
                              }}
                            >
                              <input
                                type="checkbox"
                                id={`store-${store.id}`}
                                checked={selectedStores.includes(store.id)}
                                readOnly
                                className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              />
                              <label htmlFor={`store-${store.id}`} className="cursor-pointer text-sm text-gray-900 dark:text-white">
                                {store.name}{store.storenumber && ` (${store.storenumber})`}
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-gray-500 dark:text-gray-400 text-sm">No stores found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedStores.map(storeId => {
                      const store = (Array.isArray(stores) ? stores : []).find(s => s.id === storeId);
                      return store ? (
                        <span
                          key={storeId}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {store.name}{store.storenumber && ` (${store.storenumber})`}
                          <button
                            type="button"
                            className="ml-1.5 inline-flex text-blue-500 dark:text-blue-200 hover:text-blue-700 dark:hover:text-blue-100"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleStoreToggle(storeId);
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                  {isStoreDropdownOpen && (
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={handleApplyStoresFilter}
                        className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={handleClearStoresFilter}
                        className="text-xs px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* Category Filter */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category {isLoadingCategories && <span className="text-xs text-gray-500">(Loading...)</span>}
                  </label>
                  <select
                    id="category"
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Area Coach Filter - Visible for all users */}
                <div>
                  <label htmlFor="area-coach" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Area Coach {isLoadingAreaCoaches && <span className="text-xs text-gray-500">(Loading...)</span>}
                  </label>
                  <select
                    id="area-coach"
                    value={selectedAreaCoach || ''}
                    onChange={(e) => setSelectedAreaCoach(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">All Area Coaches</option>
                    {areaCoaches.map(coach => (
                      <option key={coach.id} value={coach.id}>
                        {coach.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Job Card Required Filter - Always visible */}
              <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="job-card-required"
                    checked={showJobCardRequiredOnly}
                    onChange={(e) => setShowJobCardRequiredOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Show only requisitions requiring job cards
                  </span>
                </label>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleFilterApply}
                  disabled={isLoadingList}
                  className="px-4 py-2 bg-gradient-to-r from-brand-400 to-brand-500 text-white rounded-md font-semibold hover:from-brand-300 hover:to-brand-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoadingList && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Apply Filters
                </button>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-400 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Filter Button for Quick Mobile Access */}
        <div className="md:hidden fixed bottom-4 right-4 z-30">
          <button
            onClick={toggleMobileFilters}
            className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-brand-400 to-brand-500 text-white shadow-lg hover:from-brand-300 hover:to-brand-500 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>

        {displayError && (
          <div className="my-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
            Error: {displayError}
          </div>
        )}

        {!isLoadingList && pagination?.data.length === 0 && !displayError &&
          <div className="text-center my-4 p-4 bg-yellow-100 dark:bg-yellow-700 dark:bg-opacity-50 text-yellow-700 dark:text-yellow-200 rounded-md shadow">
            No requisitions found matching your criteria.
          </div>
        }

        {/* Requisition Count Display */}
        {pagination && pagination.data && pagination.data.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-700 dark:text-gray-300 font-medium">
              {(selectedAreaCoach || showJobCardRequiredOnly) ? (
                <span className="bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 py-1 px-3 rounded-full text-sm">
                  Showing {filterRequisitions(pagination?.data || []).length} of {pagination?.total || 0} requisitions
                </span>
              ) : (
                <span className="bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 py-1 px-3 rounded-full text-sm">
                  Showing {pagination?.data.length} requisitions
                </span>
              )}
            </div>
            {(selectedAreaCoach || showJobCardRequiredOnly) && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {selectedAreaCoach && <span className="mr-2">Area Coach Filter Active</span>}
                {showJobCardRequiredOnly && <span>Job Card Filter Active</span>}
              </div>
            )}
          </div>
        )}

        {pagination?.data && pagination.data.length > 0 && (
          viewMode === 'card'
            ? renderRequisitionCards(pagination.data)
            : renderRequisitionTable(pagination.data)
        )}

        {pagination && pagination.last_page > 1 && (
          <div className="mt-8 flex flex-col items-center">
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1 || isLoadingList}
                className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md disabled:opacity-50 transition-colors flex items-center"
              >
                {isLoadingList && pagination.current_page > 1 && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300"> Page {pagination.current_page} of {pagination.last_page} </span>
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page >= pagination.last_page || isLoadingList}
                className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md disabled:opacity-50 transition-colors flex items-center"
              >
                {isLoadingList && pagination.current_page < pagination.last_page && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Next
              </button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {(selectedAreaCoach || showJobCardRequiredOnly) ? (
                <>Filtered Items: {filterRequisitions(pagination?.data || []).length} (Total: {pagination.total})</>
              ) : (
                <>Total Items: {pagination.total}</>
              )}
            </div>
          </div>
        )}

        {/* Bulk Approve Confirmation Modal */}
        {approveModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Confirm Bulk Approve</h3>
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Are you sure you want to approve {selectedRequisitions.length} {selectedRequisitions.length === 1 ? 'requisition' : 'requisitions'}?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setApproveModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkApprove}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Reject Confirmation Modal */}
        {rejectModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Confirm Bulk Reject</h3>
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Are you sure you want to reject {selectedRequisitions.length} {selectedRequisitions.length === 1 ? 'requisition' : 'requisitions'}?
              </p>
              <div className="mb-4">
                <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Rejection (Required)
                </label>
                <textarea
                  id="rejection-reason"
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter reason for rejection"
                ></textarea>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setRejectModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={!rejectionReason.trim()} // Disable if reason is empty
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick View Modal */}
        <QuickViewModal
          requisitionId={quickViewRequisitionId}
          onClose={() => setQuickViewRequisitionId(null)}
        />
      </div>
    </div>
  );
};

export default RequisitionListPage;
